import type { Dictionary } from '../../i18n/dictionary';

/** „A”, „B” i „C” — names quoted the Polish way, joined into one phrase. */
function quotedPl(names: readonly string[]): string {
  const quoted = names.map((n) => `„${n}”`);
  return quoted.length > 1
    ? `${quoted.slice(0, -1).join(', ')} i ${quoted.at(-1) ?? ''}`
    : (quoted[0] ?? '');
}

/** "A", "B" and "C". */
function quotedEn(names: readonly string[]): string {
  const quoted = names.map((n) => `"${n}"`);
  return quoted.length > 1
    ? `${quoted.slice(0, -1).join(', ')} and ${quoted.at(-1) ?? ''}`
    : (quoted[0] ?? '');
}

const pl = {
  appName: 'Checklisty',
  titleSuffix: 'Checklisty — ITLamb',
  checklists: 'Listy',
  groups: 'Grupy',

  newChecklist: 'Nowa lista',
  editChecklist: 'Edytuj listę',
  empty: 'Nie masz jeszcze żadnych list.',
  addFirst: 'Dodaj pierwszą listę',
  progress: (done: number, total: number) => `${String(done)}/${String(total)}`,
  progressLabel: (done: number, total: number) =>
    `Odhaczono ${String(done)} z ${String(total)}`,

  kind: 'Rodzaj',
  standard: 'Zwykła',
  combined: 'Łączona',
  name: 'Nazwa',
  items: 'Pozycje',
  item: (n: number) => `Pozycja ${String(n)}`,
  addItem: 'Dodaj pozycję',
  moveUp: 'Przesuń w górę',
  moveDown: 'Przesuń w dół',
  removeItem: 'Usuń',
  children: 'Listy',
  addChild: 'Dodaj',
  addChildHeading: 'Dodaj listę',
  noStandardLists: 'Nie masz jeszcze zwykłych list, z których można złożyć tę.',
  save: 'Zapisz',
  saving: 'Zapisywanie…',
  cancel: 'Anuluj',
  edit: 'Edytuj',
  back: 'Wszystkie listy',

  emptyStandard: 'Ta lista nie ma jeszcze pozycji.',
  emptyCombined: 'Ta lista nie łączy jeszcze żadnych list.',
  emptyChild: 'Ta lista nie ma pozycji.',
  addItems: 'Dodaj pozycje',
  pickLists: 'Wybierz listy',

  group: 'Grupa',
  groupEmpty: 'W tej grupie nie ma jeszcze list.',
  groupsHint:
    'Usunąć można tylko pustą grupę. Listę przenosi się do innej grupy w jej formularzu.',
  noGroups: 'Nie masz jeszcze żadnych grup.',
  groupName: 'Nazwa grupy',
  newGroup: 'Nowa grupa',
  newGroupPlaceholder: 'np. Wyjazdy',
  addGroup: 'Dodaj',
  moveGroupUp: (name: string) => `Przesuń grupę „${name}” w górę`,
  moveGroupDown: (name: string) => `Przesuń grupę „${name}” w dół`,
  moveListUp: (name: string) => `Przesuń listę „${name}” w górę`,
  moveListDown: (name: string) => `Przesuń listę „${name}” w dół`,
  renameGroup: (name: string) => `Zmień nazwę grupy „${name}”`,
  deleteGroup: (name: string) => `Usuń grupę „${name}”`,
  confirmDeleteGroup: (name: string) => `Usunąć grupę „${name}”?`,

  reset: 'Resetuj',
  confirmReset: (name: string) =>
    `Odznaczyć wszystkie pozycje listy „${name}”?`,
  confirmResetCombined: (name: string, children: readonly string[]) =>
    `Odznaczyć wszystkie pozycje listy „${name}”? Zresetowane zostaną też listy ${quotedPl(children)}.`,
  deleteChecklist: 'Usuń listę',
  confirmDelete: (name: string) => `Usunąć listę „${name}”?`,
  confirmDeleteChild: (name: string, parents: readonly string[]) =>
    `Usunąć listę „${name}”? Zniknie też z list łączonych ${quotedPl(parents)}.`,

  loadFailed: 'Nie udało się wczytać list.',
  createFailed: 'Nie udało się dodać listy.',
  updateFailed: 'Nie udało się zapisać listy.',
  deleteFailed: 'Nie udało się usunąć listy.',
  tickFailed: 'Nie udało się zapisać odhaczenia.',
  resetFailed: 'Nie udało się zresetować listy.',
  loadGroupsFailed: 'Nie udało się wczytać grup.',
  addGroupFailed: 'Nie udało się dodać grupy.',
  renameGroupFailed: 'Nie udało się zmienić nazwy grupy.',
  deleteGroupFailed: 'Nie udało się usunąć grupy.',
  reorderFailed: 'Nie udało się zmienić kolejności.',
  groupSaved: 'Grupa zapisana.',
  created: 'Lista dodana.',
  updated: 'Lista zapisana.',
};

const en: typeof pl = {
  appName: 'Checklists',
  titleSuffix: 'Checklists — ITLamb',
  checklists: 'Lists',
  groups: 'Groups',

  newChecklist: 'New list',
  editChecklist: 'Edit list',
  empty: "You don't have any lists yet.",
  addFirst: 'Add your first list',
  progress: (done, total) => `${String(done)}/${String(total)}`,
  progressLabel: (done, total) => `${String(done)} of ${String(total)} ticked`,

  kind: 'Kind',
  standard: 'Standard',
  combined: 'Combined',
  name: 'Name',
  items: 'Items',
  item: (n) => `Item ${String(n)}`,
  addItem: 'Add item',
  moveUp: 'Move up',
  moveDown: 'Move down',
  removeItem: 'Remove',
  children: 'Lists',
  addChild: 'Add',
  addChildHeading: 'Add a list',
  noStandardLists: "You don't have any standard lists to combine yet.",
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  edit: 'Edit',
  back: 'All lists',

  emptyStandard: 'This list has no items yet.',
  emptyCombined: "This list doesn't combine any lists yet.",
  emptyChild: 'This list has no items.',
  addItems: 'Add items',
  pickLists: 'Pick lists',

  group: 'Group',
  groupEmpty: 'This group has no lists yet.',
  groupsHint:
    "Only an empty group can be deleted. A list moves to another group in the list's form.",
  noGroups: "You don't have any groups yet.",
  groupName: 'Group name',
  newGroup: 'New group',
  newGroupPlaceholder: 'e.g. Trips',
  addGroup: 'Add',
  moveGroupUp: (name) => `Move the group "${name}" up`,
  moveGroupDown: (name) => `Move the group "${name}" down`,
  moveListUp: (name) => `Move the list "${name}" up`,
  moveListDown: (name) => `Move the list "${name}" down`,
  renameGroup: (name) => `Rename the group "${name}"`,
  deleteGroup: (name) => `Delete the group "${name}"`,
  confirmDeleteGroup: (name) => `Delete the group "${name}"?`,

  reset: 'Reset',
  confirmReset: (name) => `Untick every item of "${name}"?`,
  confirmResetCombined: (name, children) =>
    `Untick every item of "${name}"? This also resets ${quotedEn(children)}.`,
  deleteChecklist: 'Delete list',
  confirmDelete: (name) => `Delete the list "${name}"?`,
  confirmDeleteChild: (name, parents) =>
    `Delete the list "${name}"? It will also disappear from the combined lists ${quotedEn(parents)}.`,

  loadFailed: 'Could not load lists.',
  createFailed: 'Could not add the list.',
  updateFailed: 'Could not save the list.',
  deleteFailed: 'Could not delete the list.',
  tickFailed: 'Could not save the tick.',
  resetFailed: 'Could not reset the list.',
  loadGroupsFailed: 'Could not load groups.',
  addGroupFailed: 'Could not add the group.',
  renameGroupFailed: 'Could not rename the group.',
  deleteGroupFailed: 'Could not delete the group.',
  reorderFailed: 'Could not change the order.',
  groupSaved: 'Group saved.',
  created: 'List added.',
  updated: 'List saved.',
};

export const checklistsDict: Dictionary<typeof pl> = { pl, en };
